import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import ResellerShell from './ResellerShell'
import Toggle from '../../components/Toggle'
import ActivationChecklist from '../../components/ActivationChecklist'
import { tournamentApi } from '../../services/api'
import { useLanguage } from '../../context/LanguageContext'
import { BilingualMarkdownEditor } from '../../components/MarkdownField'

/**
 * Package editor — single-form layout matching the regular Stay & Play
 * PackageManager style. All fields stack vertically with section headers
 * and there's ONE Save button at the bottom that fans out to the relevant
 * endpoints.
 *
 * Sections:
 *   - Basics (name, description, hero image, status)
 *   - Group size & stay (golfer tiles, nights, rooms)
 *   - Pricing (hotel cap, golf cap with apply-all toggle, per-course rates)
 *   - Courses (pick from grouping radius, per-course rounds + required toggle)
 *   - Add-ons (inline list with own create/update/delete — per-addon endpoints)
 *   - Refund policy (tier table)
 *   - Payment options (split + installments toggles)
 *
 * On Save we PUT /packages/:id (basics + stay + pricing + payment),
 * PUT /packages/:id/courses (course list + per-course caps),
 * PUT /packages/:id/refund-tiers (refund policy).
 * Addons fire individually as the user edits them.
 */
export default function PackageEdit() {
  const { t } = useLanguage()
  const { id } = useParams()
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [coursesInRadius, setCoursesInRadius] = useState([])
  const [feesCtx, setFeesCtx] = useState(null) // { paymentMode, fees: {...} }
  const [activation, setActivation] = useState(null) // { ready, items, paymentMode }
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  // EPIC #92 P5 — a managed child cannot edit economics. Default true so the
  // form is never wrongly locked if /me is slow or fails.
  const [canEditPricing, setCanEditPricing] = useState(true)

  function reload() {
    setLoading(true); setError(null)
    tournamentApi.get('/portal/reseller/me')
      .then(r => setCanEditPricing(r.data?.canEditPricing !== false))
      .catch(() => { /* non-fatal — leave editable */ })
    tournamentApi.get(`/portal/reseller/packages/${id}`)
      .then(async r => {
        setData(r.data)
        if (r.data?.package?.Grouping_ID) {
          try {
            const cr = await tournamentApi.get(`/portal/reseller/groupings/${r.data.package.Grouping_ID}/courses-in-radius`)
            setCoursesInRadius(cr.data?.courses || [])
          } catch { /* non-fatal */ }
        }
      })
      .catch(err => setError(err?.response?.data?.message || err.message))
      .finally(() => setLoading(false))
    // Fees context — fetched once; doesn't change with package selection
    tournamentApi.get('/portal/reseller/fees/effective')
      .then(r => setFeesCtx(r.data))
      .catch(() => { /* non-fatal — breakdown just won't show fees */ })
    // Activation readiness — drives the "you can't go live until..." panel.
    tournamentApi.get('/portal/reseller/packages/activation-readiness')
      .then(r => setActivation(r.data))
      .catch(() => { /* non-fatal */ })
  }
  useEffect(reload, [id])

  if (loading) return <ResellerShell title={t('sp_loading_package_title','Loading package…')}><div style={{ color: '#64748b' }}>{t('sp_loading_short','Loading…')}</div></ResellerShell>
  if (error || !data) {
    return (
      <ResellerShell title={t('sp_th_package','Package')}>
        <div style={errorBox}>{error || t('sp_not_found','Not found')}</div>
        <Link to="/portal/reseller/groupings" style={{ color: '#475569' }}>{t('sp_all_groupings','← All groupings')}</Link>
      </ResellerShell>
    )
  }

  // Back link points to the parent grouping's Packages tab so the editor
  // returns the user where they came from instead of dumping them on a
  // generic flat list.
  const groupingName = data.package.GroupingName || t('sp_grouping_fallback','grouping')
  const backHref = `/portal/reseller/groupings/${data.package.Grouping_ID}?tab=packages`

  return (
    <ResellerShell title={data.package.Name}>
      <Link to={backHref} style={{ color: '#475569', textDecoration: 'none', fontSize: 13 }}>
        {t('sp_return_to_grouping_packages','← Return to {grouping} packages',{ grouping: groupingName })}
      </Link>
      <PackageForm
        pkg={data.package}
        courses={data.courses}
        addons={data.addons}
        refundTiers={data.refundTiers}
        playRules={data.playRules}
        coursesInRadius={coursesInRadius}
        feesCtx={feesCtx}
        activation={activation}
        canEditPricing={canEditPricing}
        onReload={reload}
        onArchived={() => navigate(backHref)}
      />
    </ResellerShell>
  )
}

// ─── The single-form editor ─────────────────────────────────────────────
function PackageForm({ pkg, courses, addons, refundTiers, playRules, coursesInRadius, feesCtx, activation, canEditPricing = true, onReload, onArchived }) {
  const { t } = useLanguage()
  // EPIC #92 P5 — when this is a managed child account, economics are locked
  // (the parent sets them). Operational fields stay editable. The server is
  // the real enforcement; this disables the inputs so the child isn't met
  // with a 403 on save.
  const pricingLocked = canEditPricing === false
  // Form state — mirrors the package row + nested collections.
  // Group size is a single number (numGolfers); we still write minGolfers
  // = maxGolfers = numGolfers so the underlying schema doesn't change.
  const [f, setF] = useState({
    name:                  pkg.Name,
    nameFr:                pkg.NameFr || '',
    description:           pkg.Description || '',
    descriptionFr:         pkg.DescriptionFr || '',
    status:                pkg.Status,
    numGolfers:            pkg.MaxGolfers || pkg.MinGolfers || 2,
    nights:                pkg.Nights,
    roomCount:             pkg.RoomCount,
    roundsPerGolfer:       pkg.RoundsPerGolfer || 1,
    price:                 pkg.Price ?? '',
    // Currency override: '' = inherit the grouping's currency; 'CAD'/'USD' = override.
    currency:              pkg.Currency || '',
    maxHotelPricePerNight: pkg.MaxHotelPricePerNight ?? '',
    defaultMaxGolfPrice:   pkg.DefaultMaxGolfPrice ?? '',
    // Margin cap (maximum revenue). '' = off.
    maxRevenueCap:         pkg.MaxRevenueCap ?? '',
    // v160 package-level fallback pricing flags (used when apply-all
    // mode is on, i.e. DefaultMaxGolfPrice is the rate and per-course
    // MaxPricePerRound is NULL).
    defaultHardCap:              !!pkg.DefaultHardCap,
    defaultBillExtrasFullPrice:  !!pkg.DefaultBillExtrasFullPrice,
    allowSplitPayments:    !!pkg.AllowSplitPayments,
    allowInstallments:     !!pkg.AllowInstallments,
    includeInSearch:       pkg.IncludeInSearch == null ? true : !!pkg.IncludeInSearch,
    installmentCount:      pkg.InstallmentCount || '',
    // Play rules (Other Options section)
    forceOnePerCourse:        !!playRules?.ForceOnePerCourse,
    noBackToBackSameCourse:   !!playRules?.NoBackToBackSameCourse,
    allowSameDayMultiCourse:  playRules ? !!playRules.AllowSameDayMultiCourse : true,
    // Custom package fee override (super-admin-gated; display-only estimate).
    feeOverrideType:          pkg.FeeOverrideType || 'none',
    feeOverrideValue:         pkg.FeeOverrideValue != null ? String(pkg.FeeOverrideValue) : '',
  })
  // "Apply same max to all courses" — defaults to ON when no per-course
  // override is set yet, OFF when the package was previously editing in
  // per-course mode. The user can flip either way.
  const [applyAllCourses, setApplyAllCourses] = useState(
    !courses.some(c => c.MaxPricePerRound != null)
  )

  // Per-course rows live in their own state so add/remove/edit is local
  // until Save fires the bulk-replace.
  //
  // If the package has no per-course entries yet (newly created or seeded
  // template), pre-populate from the grouping's radius — every course is
  // selected by default; the reseller removes the ones they don't want.
  const [courseRows, setCourseRows] = useState(() => {
    if (courses.length > 0) {
      return courses.map(c => ({
        coursesId:        c.Courses_ID,
        courseName:       c.CourseName,
        city:             c.City,
        minRounds:        c.MinRounds,
        maxRounds:        c.MaxRounds,
        isRequired:       !!c.IsRequired,
        excluded:         !!c.Excluded,
        sortOrder:        c.SortOrder,
        maxPricePerRound: c.MaxPricePerRound != null ? String(c.MaxPricePerRound) : '',
        hardCap:          !!c.HardCap,
        billExtrasFullPrice: !!c.BillExtrasFullPrice,
      }))
    }
    // Empty saved list → seed with all radius courses
    return coursesInRadius.map((c, i) => ({
      coursesId:        c.Courses_ID,
      courseName:       c.CourseName,
      city:             c.City,
      minRounds:        1,
      maxRounds:        1,
      isRequired:       false,
      excluded:         false,
      sortOrder:        i,
      maxPricePerRound: '',
      hardCap:          false,
      billExtrasFullPrice: false,
    }))
  })

  // If radius loads after the form mounts (async fetch in parent) AND we
  // started with an empty saved list, sync radius courses in. Doesn't
  // override existing user edits — only fills when current rows is empty.
  useEffect(() => {
    if (courses.length === 0 && courseRows.length === 0 && coursesInRadius.length > 0) {
      setCourseRows(coursesInRadius.map((c, i) => ({
        coursesId:        c.Courses_ID,
        courseName:       c.CourseName,
        city:             c.City,
        minRounds:        1,
        maxRounds:        1,
        isRequired:       false,
        sortOrder:        i,
        maxPricePerRound: '',
        hardCap:          false,
        billExtrasFullPrice: false,
      })))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coursesInRadius.length])

  // Refund tiers live in their own state too — bulk-replaced on save.
  const [tierRows, setTierRows] = useState(
    refundTiers.length > 0
      ? refundTiers.map(t => ({
          daysBeforeMin: t.DaysBeforeMin,
          daysBeforeMax: t.DaysBeforeMax,
          refundPercent: Number(t.RefundPercent),
        }))
      : [
          { daysBeforeMin: 30, daysBeforeMax: null, refundPercent: 100 },
          { daysBeforeMin: 14, daysBeforeMax: 29,   refundPercent: 50 },
          { daysBeforeMin: 0,  daysBeforeMax: 13,   refundPercent: 0 },
        ]
  )

  const [saving, setSaving] = useState(false)
  const [contentLang, setContentLang] = useState('en') // shared EN/FR toggle for name + description
  const [err, setErr] = useState(null)
  const [savedFlash, setSavedFlash] = useState(false)
  // Dedicated "Update fees" button state (persists just the fee override).
  const [feeSaving, setFeeSaving] = useState(false)
  const [feeSaveStatus, setFeeSaveStatus] = useState(null) // null | 'saved' | 'error:<msg>'

  // Hotel availability check — fires on lost focus of the hotel-max input.
  // Uses the grouping's center+radius and these inputs to query Expedia.
  const [hotelCheck, setHotelCheck] = useState(null)
  const [hotelChecking, setHotelChecking] = useState(false)
  async function checkHotelAvailability() {
    const budget = Number(f.maxHotelPricePerNight)
    const nights = Number(f.nights) || 1
    if (!budget || budget <= 0) { setHotelCheck(null); return }
    setHotelChecking(true)
    try {
      const { data } = await tournamentApi.get(
        `/portal/reseller/groupings/${pkg.Grouping_ID}/hotel-count`,
        { params: { budget, nights, minStars: 3 } }
      )
      setHotelCheck(data)
    } catch (e) {
      setHotelCheck({ available: 0, message: t('sp_could_not_check_hotel_avail','Could not check hotel availability.') })
    } finally { setHotelChecking(false) }
  }

  function set(k, v) { setF(prev => ({ ...prev, [k]: v })) }
  function num(v) { return (v === '' || v == null) ? null : Number(v) }

  // Live cost breakdown — same math the visible CostBreakdown uses, lifted
  // here so the form can disable the "Active" status when the package would
  // lose money at the worst-case caps.
  const breakdown = computeBreakdown({
    price:                 f.price,
    nights:                f.nights,
    roomCount:             f.roomCount,
    numGolfers:            f.numGolfers,
    roundsPerGolfer:       f.roundsPerGolfer,
    maxHotelPricePerNight: f.maxHotelPricePerNight,
    defaultMaxGolfPrice:   f.defaultMaxGolfPrice,
    applyAllCourses,
    courseRows,
    feesCtx,
    feeOverrideType:       f.feeOverrideType,
    feeOverrideValue:      f.feeOverrideValue,
  })
  const losingMoney = !!(breakdown && breakdown.isNegative)
  // If the user previously had status='active' and pricing went negative,
  // bounce it back to 'draft' so the form state stays internally consistent.
  useEffect(() => {
    if (losingMoney && f.status === 'active') setF(prev => ({ ...prev, status: 'draft' }))
  }, [losingMoney])

  // Courses available to add: in radius but not yet in the row list.
  const selectedIds = new Set(courseRows.map(r => r.coursesId))
  const addable = coursesInRadius.filter(c => !selectedIds.has(c.Courses_ID))

  function updateCourseRow(i, k, v) {
    setCourseRows(rs => rs.map((r, idx) => idx === i ? { ...r, [k]: v } : r))
  }
  // Required and Excluded are mutually exclusive — turning one on clears the
  // other (an excluded course is hidden from customers, so it can't be
  // mandatory).
  function setExclusiveFlag(i, flag, next) {
    setCourseRows(rs => rs.map((r, idx) => {
      if (idx !== i) return r
      if (flag === 'excluded')   return { ...r, excluded: next, isRequired: next ? false : r.isRequired }
      if (flag === 'isRequired') return { ...r, isRequired: next, excluded: next ? false : r.excluded }
      return r
    }))
  }
  function removeCourse(i) { setCourseRows(rs => rs.filter((_, idx) => idx !== i)) }
  function addCourse(c) {
    setCourseRows(rs => [...rs, {
      coursesId:        c.Courses_ID,
      courseName:       c.CourseName,
      city:             c.City,
      minRounds:        1,
      maxRounds:        1,
      isRequired:       false,
      excluded:         false,
      sortOrder:        rs.length,
      maxPricePerRound: '',
      hardCap:          false,
      billExtrasFullPrice: false,
    }])
  }

  // Auto-save course list whenever it changes. Resellers were getting
  // confused by the candidate-picker UX -- they thought toggling a course
  // 'added it to the package' but the row was only in form state until the
  // manual Save button was clicked. Embed then showed empty round picker.
  // Debounced 600ms so rapid toggles batch into one PUT. Skip the initial
  // mount so loading the editor doesn't trigger a no-op save.
  const initialCourseLoadDone = useRef(false)
  const [courseSaveStatus, setCourseSaveStatus] = useState('') // '' | 'saving' | 'saved' | 'error: ...'
  useEffect(() => {
    if (!pkg) return
    if (!initialCourseLoadDone.current) { initialCourseLoadDone.current = true; return }
    const handle = setTimeout(async () => {
      setCourseSaveStatus('saving')
      try {
        await tournamentApi.put(`/portal/reseller/packages/${pkg.ID_NO}/courses`, {
          courses: courseRows.map((r, i) => ({
            coursesId:        r.coursesId,
            minRounds:        Number(r.minRounds) || 1,
            maxRounds:        Number(r.maxRounds) || 1,
            maxPricePerRound: applyAllCourses ? null : (r.maxPricePerRound === '' || r.maxPricePerRound == null ? null : Number(r.maxPricePerRound)),
            // Hard cap only meaningful when there's an actual cap to
            // enforce; if applyAllCourses is on the per-course cap is
            // null and the hard cap is meaningless, so don't send it.
            hardCap:          !applyAllCourses && !!r.hardCap,
            // billExtrasFullPrice also only meaningful with a per-course
            // cap (extras pricing references MaxPricePerRound).
            billExtrasFullPrice: !applyAllCourses && !!r.billExtrasFullPrice,
            excluded:         !!r.excluded,
            isRequired:       !r.excluded && !!r.isRequired,
            sortOrder:        i,
          })),
        })
        setCourseSaveStatus('saved')
        setTimeout(() => setCourseSaveStatus(s => s === 'saved' ? '' : s), 1500)
      } catch (err) {
        setCourseSaveStatus('error: ' + (err?.response?.data?.message || err.message || 'save failed'))
      }
    }, 600)
    return () => clearTimeout(handle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseRows, applyAllCourses])

  function updateTier(i, k, v) { setTierRows(rs => rs.map((r, j) => j === i ? { ...r, [k]: v } : r)) }
  function removeTier(i) { setTierRows(rs => rs.filter((_, j) => j !== i)) }
  function addTier() { setTierRows(rs => [...rs, { daysBeforeMin: 0, daysBeforeMax: null, refundPercent: 0 }]) }

  // ─── Save: fans out to 3 endpoints ─────────────────────────────────────
  async function save() {
    // Belt-and-suspenders: even if the dropdown's `disabled` got bypassed,
    // never persist status='active' on a package that's losing money.
    const safeStatus = (losingMoney && f.status === 'active') ? 'draft' : f.status
    setErr(null); setSaving(true); setSavedFlash(false)
    try {
      // 1. Package core fields (basics + stay + pricing + payment).
      // numGolfers fans out to min=max in the schema so existing range-based
      // queries don't need to change.
      const n = Number(f.numGolfers) || 2
      await tournamentApi.put(`/portal/reseller/packages/${pkg.ID_NO}`, {
        name:                  f.name,
        nameFr:                f.nameFr,
        description:           f.description,
        descriptionFr:         f.descriptionFr,
        status:                safeStatus,
        minGolfers:            n,
        maxGolfers:            n,
        nights:                Number(f.nights) || 1,
        roomCount:             Number(f.roomCount) || 1,
        roundsPerGolfer:       Number(f.roundsPerGolfer) || 1,
        price:                 num(f.price),
        currency:              f.currency || null, // '' => inherit the grouping currency
        maxHotelPricePerNight: num(f.maxHotelPricePerNight),
        // Margin cap: '' / off => null (clears it); otherwise the ceiling.
        maxRevenueCap:         f.maxRevenueCap === '' || f.maxRevenueCap == null ? null : num(f.maxRevenueCap),
        defaultMaxGolfPrice:   applyAllCourses ? num(f.defaultMaxGolfPrice) : null,
        // v160 package-level fallback flags — only meaningful in apply-all mode.
        // When NOT in apply-all mode we clear them so they don't accidentally
        // affect anything if the user toggles modes later.
        defaultHardCap:              applyAllCourses ? (f.defaultHardCap ? 1 : 0) : 0,
        defaultBillExtrasFullPrice:  applyAllCourses ? (f.defaultBillExtrasFullPrice ? 1 : 0) : 0,
        allowSplitPayments:    f.allowSplitPayments ? 1 : 0,
        allowInstallments:     f.allowInstallments ? 1 : 0,
        includeInSearch:       f.includeInSearch ? 1 : 0,
        installmentCount:      f.installmentCount === '' ? null : Number(f.installmentCount),
        // Custom package fee override (ignored server-side unless the account is
        // super-admin-enabled). Value is the % when type=percent, $ when flat.
        feeOverrideType:       f.feeOverrideType || 'none',
        feeOverrideValue:      (f.feeOverrideType === 'none' || f.feeOverrideValue === '') ? null : Number(f.feeOverrideValue),
      })
      // 2. Courses (bulk replace) — always send so per-course caps reflect toggle state.
      await tournamentApi.put(`/portal/reseller/packages/${pkg.ID_NO}/courses`, {
        courses: courseRows.map((r, i) => ({
          coursesId:        r.coursesId,
          minRounds:        Number(r.minRounds) || 1,
          maxRounds:        Number(r.maxRounds) || 1,
          maxPricePerRound: applyAllCourses ? null : num(r.maxPricePerRound),
          hardCap:          !applyAllCourses && !!r.hardCap,
          billExtrasFullPrice: !applyAllCourses && !!r.billExtrasFullPrice,
          excluded:         !!r.excluded,
          isRequired:       !r.excluded && !!r.isRequired,
          sortOrder:        i,
        })),
      })
      // 3. Refund tiers (bulk replace)
      await tournamentApi.put(`/portal/reseller/packages/${pkg.ID_NO}/refund-tiers`, {
        tiers: tierRows.map(r => ({
          daysBeforeMin: Number(r.daysBeforeMin) || 0,
          daysBeforeMax: r.daysBeforeMax === '' || r.daysBeforeMax == null ? null : Number(r.daysBeforeMax),
          refundPercent: Number(r.refundPercent) || 0,
        })),
      })
      // 4. Play rules (Other Options) — upsert
      await tournamentApi.put(`/portal/reseller/packages/${pkg.ID_NO}/play-rules`, {
        forceOnePerCourse:       f.forceOnePerCourse,
        noBackToBackSameCourse:  f.noBackToBackSameCourse,
        allowSameDayMultiCourse: f.allowSameDayMultiCourse,
      })
      setSavedFlash(true)
      setTimeout(() => setSavedFlash(false), 2500)
      onReload()
    } catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setSaving(false) }
  }

  // ─── Update fees: persist ONLY the fee override ─────────────────────────
  // Partial save (the package PUT only writes fields present in the body), so
  // it commits the override without touching the reseller's other unsaved
  // edits or tripping the activation/pricing gates. The cost breakdown above
  // already re-renders live from local state, so no reload is needed.
  async function saveFeeOverride() {
    setFeeSaving(true); setFeeSaveStatus(null)
    try {
      await tournamentApi.put(`/portal/reseller/packages/${pkg.ID_NO}`, {
        feeOverrideType:  f.feeOverrideType || 'none',
        feeOverrideValue: (f.feeOverrideType === 'none' || f.feeOverrideValue === '') ? null : Number(f.feeOverrideValue),
      })
      setFeeSaveStatus('saved')
      setTimeout(() => setFeeSaveStatus(s => (s === 'saved' ? null : s)), 2500)
    } catch (e) {
      setFeeSaveStatus('error:' + (e?.response?.data?.message || e.message))
    } finally { setFeeSaving(false) }
  }

  async function archive() {
    if (!confirm(t('sp_confirm_archive_package','Archive this package? You can unarchive it later.'))) return
    try { await tournamentApi.delete(`/portal/reseller/packages/${pkg.ID_NO}`); onArchived() }
    catch (e) { alert(e?.response?.data?.message || e.message) }
  }

  // Hard delete — only offered when the package has no real booking history.
  // We pre-check with the server so we can explain exactly why if it's blocked,
  // then require the user to type the package name (irreversible).
  async function deletePermanently() {
    let info
    try {
      const { data } = await tournamentApi.get(`/portal/reseller/packages/${pkg.ID_NO}/deletability`)
      info = data
    } catch (e) { alert(e?.response?.data?.message || e.message); return }
    if (!info?.canDelete) {
      const list = Object.entries(info?.breakdown || {}).map(([s, n]) => `${n} ${s}`).join(', ')
      alert(t('sp_delete_pkg_blocked',
        'This package can’t be deleted because it has real bookings ({list}). Archive it instead.',
        { list: list || t('sp_bookings_word', 'bookings') }))
      return
    }
    const cartNote = info.abandonedCarts
      ? t('sp_delete_pkg_carts_note', ' {n} abandoned cart(s) will be detached.', { n: info.abandonedCarts })
      : ''
    const word = t('sp_confirm_word', 'CONFIRM')
    const typed = prompt(t('sp_delete_pkg_prompt',
      'This permanently deletes “{name}” and its setup. This cannot be undone.{carts}\n\nType {word} to confirm:',
      { name: pkg.Name, carts: cartNote, word }))
    if (typed == null) return
    if (!['confirm', 'confirmer', 'yes', 'oui'].includes(typed.trim().toLowerCase())) {
      alert(t('sp_delete_not_confirmed', 'Not confirmed — nothing was deleted.'))
      return
    }
    try {
      await tournamentApi.delete(`/portal/reseller/packages/${pkg.ID_NO}/permanent`)
      onArchived()  // same behaviour as archive: return to the packages list
    } catch (e) { alert(e?.response?.data?.message || e.message) }
  }

  return (
    <div style={{ marginTop: 16 }}>
      {err && <div style={errorBox}>{err}</div>}

      {/* ─── Basics ──────────────────────────────────────────────────── */}
      <SectionHeader>{t('sp_basics','Basics')}</SectionHeader>
      {/* One EN/FR toggle for all bilingual content on this page — flips the
          name field AND the description editor together. */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {[['en', t('sp_lang_english', 'English')], ['fr', t('sp_lang_french', 'Français')]].map(([code, label]) => (
          <button key={code} type="button" onClick={() => setContentLang(code)}
            style={{
              padding: '5px 14px', fontSize: 13, fontWeight: 600, borderRadius: 6, cursor: 'pointer',
              background: contentLang === code ? '#0f172a' : '#fff',
              color: contentLang === code ? '#fff' : '#475569',
              border: '1px solid ' + (contentLang === code ? '#0f172a' : '#e2e8f0'),
            }}>{label}</button>
        ))}
      </div>
      <Field label={t('sp_package_name','Package name')}>
        {contentLang === 'en'
          ? <input style={inp} value={f.name} onChange={e => set('name', e.target.value)} placeholder={t('sp_package_name_en_ph', 'Package name')} />
          : <input style={inp} value={f.nameFr} onChange={e => set('nameFr', e.target.value)} placeholder={t('sp_package_name_fr_ph', 'Nom du forfait')} />}
      </Field>
      <Field label={t('sp_description','Description')}>
        <BilingualMarkdownEditor
          lang={contentLang} onLangChange={setContentLang}
          valueEn={f.description} valueFr={f.descriptionFr}
          onChangeEn={v => set('description', v)} onChangeFr={v => set('descriptionFr', v)}
          placeholderEn={t('sp_description_placeholder','What makes this package special?')}
          placeholderFr={t('sp_description_placeholder_fr','Qu’est-ce qui rend ce forfait spécial ?')}
        />
      </Field>
      {/* Activation readiness checklist. Shown whenever any item is
          unmet (required OR warning). Required items block activation
          (rendered as red); warnings don't block but flag follow-up
          work like adding bank info before the first weekly EFT. */}
      <ActivationChecklist activation={activation} />


      <div style={row}>
        <Field
          label={t('status','Status')}
          style={{ flex: 1 }}
          hint={
            losingMoney
              ? t('sp_hint_cannot_activate_loss','⚠ Cannot activate while the cost breakdown shows a loss. Raise the package price or lower the hotel/golf caps.')
              : (activation && !activation.ready)
                ? t('sp_hint_payment_setup_incomplete','⚠ Payment setup incomplete (see checklist above).')
                : undefined
          }
        >
          <select
            value={f.status}
            onChange={e => set('status', e.target.value)}
            style={{ ...inp, ...((losingMoney || (activation && !activation.ready)) ? { borderColor: '#fecaca' } : {}) }}
          >
            <option value="draft">{t('draft','Draft')}</option>
            <option value="active" disabled={losingMoney || (activation && !activation.ready)}>
              {t('active','Active')}{losingMoney ? t('sp_status_active_locked_breakdown',' — locked, fix the breakdown') : (activation && !activation.ready) ? t('sp_status_active_locked_payment',' — locked, finish payment setup') : ''}
            </option>
            <option value="paused">{t('paused','Paused')}</option>
          </select>
        </Field>
        <Field label={t('sp_slug','Slug')} style={{ flex: 1 }}>
          <input value={pkg.Slug} readOnly style={{ ...inp, background: '#f8fafc', color: '#94a3b8', fontFamily: 'monospace' }} />
        </Field>
      </div>

      {/* Per-package opt-in to the account-wide search iframe. Default
          on; reseller can hide individual packages from the search
          widget without affecting bookability via the per-grouping
          iframe. The iframe-header toggle lives at the GROUPING level
          (Grouping → Embed tab) since header chrome is grouping-wide. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 16 }}>
        <Toggle checked={f.includeInSearch} onChange={(next) => set('includeInSearch', next)} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', cursor: 'pointer' }} onClick={() => set('includeInSearch', !f.includeInSearch)}>
            {t('sp_include_in_search_widget','Include in your account-wide search widget')}
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
            {t('sp_include_in_search_desc','When on, this package appears in the cross-grouping search iframe customers can put on their site. Turn off to hide just this package while keeping it bookable through its grouping iframe.')}
          </div>
        </div>
      </div>

      {/* ─── Group size + stay ───────────────────────────────────────── */}
      <SectionHeader>{t('sp_section_group_size_stay','Group size & stay')}</SectionHeader>
      <Field label={t('sp_label_golfers_per_booking','How many golfers per booking?')}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 8 }}>
          {[1,2,3,4,5,6,7,8].map(n => {
            const active = Number(f.numGolfers) === n
            return (
              <button key={n} type="button"
                onClick={() => set('numGolfers', n)}
                style={{
                  padding: '12px 8px',
                  border: '2px solid ' + (active ? '#00d19f' : '#e2e8f0'),
                  borderRadius: 8,
                  background: active ? '#f0fdfa' : '#fff',
                  cursor: 'pointer', textAlign: 'center', fontFamily: 'inherit',
                }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: active ? '#0f766e' : '#0f172a' }}>{n}</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>{n === 1 ? t('sp_golfer_lower','golfer') : t('sp_golfers_lower','golfers')}</div>
              </button>
            )
          })}
        </div>
      </Field>
      <div style={row}>
        <Field label={t('sp_nights_cap','Nights')} style={{ flex: 1 }}>
          <input type="number" min={1} max={30} style={inp} value={f.nights} onChange={e => set('nights', Number(e.target.value))} />
        </Field>
        <Field label={t('sp_label_hotel_rooms','Hotel rooms')} style={{ flex: 1 }}>
          <input type="number" min={1} max={20} style={inp} value={f.roomCount} onChange={e => set('roomCount', Number(e.target.value))} />
        </Field>
        <Field label={t('sp_label_rounds_per_golfer','Rounds per golfer')} style={{ flex: 1 }} hint={t('sp_hint_rounds_per_golfer','How many rounds each golfer plays across the package.')}>
          <input type="number" min={1} max={20} style={inp} value={f.roundsPerGolfer} onChange={e => set('roundsPerGolfer', Number(e.target.value))} />
        </Field>
      </div>

      {/* ─── Pricing ─────────────────────────────────────────────────── */}
      <SectionHeader>{t('sp_pricing','Pricing')}</SectionHeader>

      {pricingLocked && (
        <div style={{ marginBottom: 16, padding: '10px 14px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, fontSize: 13, color: '#1e40af', lineHeight: 1.5 }}>
          {t('sp_pricing_locked_notice', 'Pricing is set by your partner account and can’t be changed here. You can still edit the package name, description, photos, included courses, availability, and play rules.')}
        </div>
      )}

      <Field label={t('sp_label_package_price','Package price (total customer pays)')} hint={t('sp_hint_package_price','The fixed price the customer pays for this whole package — hotel + rounds + add-ons all bundled.')}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 16, color: '#94a3b8' }}>$</span>
            <input type="number" step="0.01" min={0} disabled={pricingLocked}
              style={{ ...inp, width: 240, paddingLeft: 26, fontSize: 16 }}
              placeholder="299.00"
              value={f.price}
              onChange={e => set('price', e.target.value)} />
          </div>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{f.currency || pkg.GroupingCurrency || 'CAD'}</span>
        </div>
        {Number(f.price) > 0 && Number(f.numGolfers) > 1 && (
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
            {t('sp_thats_per_person_pre',"That's")} <strong>${(Number(f.price) / Number(f.numGolfers)).toFixed(2)} {f.currency || pkg.GroupingCurrency || 'CAD'}</strong> {t('sp_per_person_for','per person for')} {f.numGolfers} {t('sp_golfers_lower','golfers')}.
          </div>
        )}
      </Field>

      <Field label={t('sp_label_package_currency','Currency')} hint={t('sp_hint_package_currency','Defaults to this region’s currency. Override only if this package is priced in a different currency.')}>
        <select disabled={pricingLocked} style={{ ...inp, width: 260 }} value={f.currency} onChange={e => set('currency', e.target.value)}>
          <option value="">{t('sp_currency_inherit','Inherit from region')} ({pkg.GroupingCurrency || 'CAD'})</option>
          <option value="CAD">CAD</option>
          <option value="USD">USD</option>
        </select>
      </Field>

      <Field label={t('sp_label_max_hotel_price','Max hotel price per night')} hint={t('sp_hint_max_hotel_price','Customer can only select hotels at or under this price. Leave blank for no cap.')}>
        <input type="number" step="0.01" min={0} disabled={pricingLocked} style={{ ...inp, width: 220 }}
          placeholder="200.00" value={f.maxHotelPricePerNight}
          onChange={e => set('maxHotelPricePerNight', e.target.value)}
          onBlur={checkHotelAvailability}
        />
        <HotelAvailabilityHint check={hotelCheck} checking={hotelChecking} groupingId={pkg.Grouping_ID} />
      </Field>

      <Field label={t('sp_label_golf_price_per_round','Golf price per round')}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <Toggle checked={applyAllCourses} onChange={setApplyAllCourses} />
          <span style={{ fontSize: 13, color: '#0f172a', cursor: 'pointer' }} onClick={() => setApplyAllCourses(v => !v)}>
            {t('sp_apply_same_rate_all_courses','Apply the same rate to all courses in this package')}
          </span>
        </div>
        {applyAllCourses ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: '#94a3b8' }}>$</span>
              <input type="number" step="0.01" min={0} disabled={pricingLocked} style={{ ...inp, width: 140 }}
                placeholder="80.00" value={f.defaultMaxGolfPrice}
                onChange={e => set('defaultMaxGolfPrice', e.target.value)} />
            </div>
            {/* v160: pricing-mode + extras-at-market apply to ALL courses
                in apply-all mode (per-course MaxPricePerRound is NULL,
                so the package-level defaults are what the embed reads). */}
            {(() => {
              const rateSet = f.defaultMaxGolfPrice !== '' && f.defaultMaxGolfPrice != null && Number(f.defaultMaxGolfPrice) > 0
              return (
                <>
                  <select
                    value={f.defaultHardCap ? 'flat' : 'cap'}
                    onChange={e => set('defaultHardCap', e.target.value === 'flat')}
                    disabled={pricingLocked || !rateSet || (!feesCtx?.hardCapAllowed && !f.defaultHardCap)}
                    style={{
                      ...inp, width: 110,
                      color: rateSet ? '#0f172a' : '#94a3b8',
                      cursor: rateSet ? 'pointer' : 'not-allowed',
                    }}
                    title={rateSet
                      ? (feesCtx?.hardCapAllowed
                          ? t('sp_title_cap_flat_explainer','Cap = up to this rate (premium upcharge above). Flat = exactly this rate (you absorb variance).')
                          : t('sp_title_flat_requires_approval','Flat-rate pricing requires platform approval.'))
                      : t('sp_title_set_rate_first','Set a rate first.')}
                  >
                    <option value="cap">{t('sp_pricing_cap','Cap')}</option>
                    {feesCtx?.hardCapAllowed && <option value="flat">{t('sp_mode_flat','Flat')}</option>}
                  </select>
                  <label
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      fontSize: 13,
                      color: rateSet ? '#0f172a' : '#94a3b8',
                      cursor: rateSet ? 'pointer' : 'not-allowed',
                    }}
                    title={rateSet
                      ? t('sp_title_extras_market_explainer','Extras beyond the package allowance bill at actual tee-time price (customer sees +$ premium on those pickers).')
                      : t('sp_title_set_rate_first','Set a rate first.')}
                  >
                    <input
                      type="checkbox"
                      checked={!!f.defaultBillExtrasFullPrice}
                      onChange={e => set('defaultBillExtrasFullPrice', e.target.checked)}
                      disabled={pricingLocked || !rateSet}
                    />
                    {t('sp_extras_at_market_price','Extras at market price')}
                  </label>
                </>
              )
            })()}
          </div>
        ) : courseRows.length === 0 ? (
          <div style={{ padding: 12, background: '#fef3c7', color: '#92400e', borderRadius: 6, fontSize: 13 }}>
            {t('sp_no_courses_linked_yet','No courses linked yet — add some below to set per-course rates.')}
          </div>
        ) : (
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: '#f8fafc' }}>
                <tr>
                  <th style={th}>{t('sp_package_th_course','Course')}</th>
                  <th style={{ ...th, width: 140 }}>{t('sp_col_round_rate','Round rate')}</th>
                  <th style={{ ...th, width: 130 }} title={t('sp_title_pricing_col_explainer','Cap = customer pays up to this rate; premium tee times add an upcharge. Flat = customer always pays exactly this rate; you absorb any variance.')}>
                    {t('sp_pricing','Pricing')}
                  </th>
                  <th style={{ ...th, width: 200 }} title={t('sp_title_extras_col_explainer','When on, rounds beyond the package allowance pay the actual tee-time price (customer sees a +$ premium badge) instead of the round rate above.')}>
                    {t('sp_extras_at_market_price','Extras at market price')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {courseRows.map((r, i) => {
                  const rateSet = r.maxPricePerRound !== '' && r.maxPricePerRound != null && Number(r.maxPricePerRound) > 0
                  return (
                    <tr key={r.coursesId} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={td}>
                        <strong style={{ fontSize: 14, color: '#0f172a' }}>{r.courseName}</strong>
                        {r.city && <div style={{ fontSize: 12, color: '#64748b' }}>{r.city}</div>}
                      </td>
                      <td style={td}>
                        <span style={{ color: '#94a3b8', marginRight: 4 }}>$</span>
                        <input type="number" step="0.01" min={0} disabled={pricingLocked}
                          style={{ ...inp, width: 100, display: 'inline-block' }}
                          placeholder={t('sp_ph_no_cap','no cap')}
                          value={r.maxPricePerRound}
                          onChange={e => updateCourseRow(i, 'maxPricePerRound', e.target.value)} />
                      </td>
                      <td style={td}>
                        <select
                          value={r.hardCap ? 'flat' : 'cap'}
                          onChange={e => updateCourseRow(i, 'hardCap', e.target.value === 'flat')}
                          disabled={pricingLocked || !rateSet || (!feesCtx?.hardCapAllowed && !r.hardCap)}
                          style={{
                            ...inp, width: 110,
                            color: rateSet ? '#0f172a' : '#94a3b8',
                            cursor: rateSet ? 'pointer' : 'not-allowed',
                          }}
                          title={rateSet
                            ? (feesCtx?.hardCapAllowed
                                ? t('sp_title_cap_flat_explainer','Cap = up to this rate (premium upcharge above). Flat = exactly this rate (you absorb variance).')
                                : t('sp_title_flat_requires_approval','Flat-rate pricing requires platform approval.'))
                            : t('sp_title_set_round_rate_first','Set a round rate first.')}
                        >
                          <option value="cap">{t('sp_pricing_cap','Cap')}</option>
                          {feesCtx?.hardCapAllowed && <option value="flat">{t('sp_mode_flat','Flat')}</option>}
                        </select>
                      </td>
                      <td style={td}>
                        <label
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            fontSize: 13,
                            color: rateSet ? '#0f172a' : '#94a3b8',
                            cursor: rateSet ? 'pointer' : 'not-allowed',
                          }}
                          title={rateSet
                            ? t('sp_title_extras_market_explainer_course','Extras beyond the package allowance are billed at the actual tee-time price; customer sees a +$ premium badge on those pickers.')
                            : t('sp_title_set_round_rate_first','Set a round rate first.')}
                        >
                          <input
                            type="checkbox"
                            checked={!!r.billExtrasFullPrice}
                            onChange={e => updateCourseRow(i, 'billExtrasFullPrice', e.target.checked)}
                            disabled={pricingLocked || !rateSet}
                          />
                          {t('sp_extras_at_market_price','Extras at market price')}
                        </label>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {!feesCtx?.hardCapAllowed && (
              <div style={{ padding: '10px 14px', fontSize: 12, color: '#64748b', background: '#f8fafc', borderTop: '1px solid #f1f5f9' }}>
                {t('sp_flat_requires_approval_contact','Flat-rate pricing requires platform approval. Contact your account manager to enable it.')}
              </div>
            )}
          </div>
        )}
      </Field>

      {/* Margin cap (maximum revenue). When on, a cheap sourcing day is passed
          back to the customer so you never net more than this ceiling. */}
      <Field label={t('sp_maxrev_label','Maximum revenue per booking')} hint={t('sp_maxrev_help',"When your net (package price − hotel − golf − InteGolf fees) would exceed this, we automatically discount the customer so you never make more than this — and show them the saving. A cheap sourcing day passes the difference back to the customer.")}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: (f.maxRevenueCap !== '' && f.maxRevenueCap != null) ? 12 : 0 }}>
          <Toggle
            checked={f.maxRevenueCap !== '' && f.maxRevenueCap != null}
            onChange={(on) => set('maxRevenueCap', on ? (f.maxRevenueCap || '200') : '')}
            disabled={pricingLocked}
          />
          <span style={{ fontSize: 13, color: '#0f172a', cursor: pricingLocked ? 'default' : 'pointer' }}
            onClick={() => { if (!pricingLocked) set('maxRevenueCap', (f.maxRevenueCap !== '' && f.maxRevenueCap != null) ? '' : (f.maxRevenueCap || '200')) }}>
            {t('sp_maxrev_toggle','Cap my maximum revenue')}
          </span>
        </div>
        {f.maxRevenueCap !== '' && f.maxRevenueCap != null && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ color: '#94a3b8' }}>$</span>
            <input type="number" step="0.01" min={0} disabled={pricingLocked} style={{ ...inp, width: 140 }}
              placeholder="200.00" value={f.maxRevenueCap} onChange={e => set('maxRevenueCap', e.target.value)} />
          </div>
        )}
      </Field>

      {/* ─── Cost breakdown (auto-computed worst-case margin) ────────── */}
      <CostBreakdown
        price={f.price}
        nights={f.nights}
        roomCount={f.roomCount}
        numGolfers={f.numGolfers}
        roundsPerGolfer={f.roundsPerGolfer}
        maxHotelPricePerNight={f.maxHotelPricePerNight}
        defaultMaxGolfPrice={f.defaultMaxGolfPrice}
        maxRevenueCap={f.maxRevenueCap}
        applyAllCourses={applyAllCourses}
        courseRows={courseRows}
        feesCtx={feesCtx}
        feeOverrideType={f.feeOverrideType}
        feeOverrideValue={f.feeOverrideValue}
      />

      {/* Custom fee override — shown whenever the super admin has enabled it for
          this reseller account. Not gated on pricing-edit rights: the capability
          is the authoritative per-account gate, and the override is a display-
          only estimate for now. */}
      {feesCtx?.canOverridePackageFee && (
        <>
          <FeeOverrideEditor
            type={f.feeOverrideType}
            value={f.feeOverrideValue}
            customerTotal={breakdown?.customerTotal || 0}
            split={feesCtx?.feeOverrideSplit || { integolfPct: 100, resellerPct: 0 }}
            onChange={(type, value) => setF(prev => ({ ...prev, feeOverrideType: type, feeOverrideValue: value }))}
          />
          <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              type="button"
              onClick={saveFeeOverride}
              disabled={feeSaving}
              style={{ ...primaryBtn, opacity: feeSaving ? 0.6 : 1 }}
            >
              {feeSaving ? t('sp_fee_override_updating','Updating…') : t('sp_fee_override_update','Update fees')}
            </button>
            {feeSaveStatus === 'saved' && (
              <span style={{ color: '#166534', fontSize: 13, fontWeight: 600 }}>{t('sp_saved_check','✓ Saved')}</span>
            )}
            {typeof feeSaveStatus === 'string' && feeSaveStatus.startsWith('error:') && (
              <span style={{ color: '#b91c1c', fontSize: 13 }}>{feeSaveStatus.slice(6)}</span>
            )}
          </div>
        </>
      )}

      {/* ─── Courses ─────────────────────────────────────────────────── */}
      <SectionHeader>
        {t('sp_section_courses_count','Courses ({count})',{ count: courseRows.length })}
        {courseSaveStatus && (
          <span style={{
            marginLeft: 10, fontSize: 12, fontWeight: 500,
            color: courseSaveStatus.startsWith('error')
              ? '#b91c1c'
              : courseSaveStatus === 'saving' ? '#64748b' : '#15803d',
          }}>
            {courseSaveStatus === 'saving' ? t('sp_course_save_saving','· saving…')
              : courseSaveStatus === 'saved' ? t('sp_course_save_saved','· ✓ saved')
              : '· ' + courseSaveStatus}
          </span>
        )}
      </SectionHeader>
      {courseRows.length === 0 ? (
        <div style={empty}>{t('sp_no_courses_selected_yet','No courses selected yet. Add from the list below.')}</div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', marginBottom: 12 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f8fafc' }}>
              <tr>
                <th style={th}>{t('sp_package_th_course','Course')}</th>
                <th style={th}>{t('sp_package_th_required','Required')}</th>
                <th style={th}>{t('sp_package_th_exclude','Exclude from package')}</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {courseRows.map((r, i) => (
                <tr key={r.coursesId} style={{ borderTop: '1px solid #f1f5f9', opacity: r.excluded ? 0.6 : 1 }}>
                  <td style={td}>
                    <strong>{r.courseName}</strong>
                    <div style={{ fontSize: 12, color: '#64748b' }}>{r.city}</div>
                  </td>
                  <td style={td}><Toggle size="sm" checked={!!r.isRequired} disabled={!!r.excluded} onChange={(next) => setExclusiveFlag(i, 'isRequired', next)} /></td>
                  <td style={td}>
                    <Toggle size="sm" checked={!!r.excluded} onChange={(next) => setExclusiveFlag(i, 'excluded', next)} />
                    {r.excluded && <span style={{ marginLeft: 8, fontSize: 11, color: '#94a3b8' }}>{t('sp_package_excluded_hint','Hidden from customers')}</span>}
                  </td>
                  <td style={td}><button onClick={() => removeCourse(i)} style={{ ...secondaryBtn, color: '#dc2626', borderColor: '#fecaca' }}>{t('sp_remove','Remove')}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {addable.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: '#64748b', alignSelf: 'center', marginRight: 6 }}>
            {t('sp_available_in_grouping','Available in this grouping ({count}):',{ count: addable.length })}
          </span>
          {addable.map(c => (
            <button key={c.Courses_ID} onClick={() => addCourse(c)} style={{ ...secondaryBtn, padding: '6px 12px' }}>
              + {c.CourseName}
              {c.DistanceKm != null && <span style={{ color: '#94a3b8', marginLeft: 4 }}>({c.DistanceKm}km)</span>}
            </button>
          ))}
        </div>
      )}

      {/* ─── Add-ons (own CRUD; doesn't share Save) ──────────────────── */}
      <SectionHeader>{t('sp_addons_optional_upsells','Add-ons (optional upsells)')}</SectionHeader>
      <AddonsList pkg={pkg} addons={addons} onChanged={onReload} locked={pricingLocked} />

      {/* ─── Refund policy ───────────────────────────────────────────── */}
      <SectionHeader>{t('sp_refund_policy','Refund policy')}</SectionHeader>
      <p style={{ color: '#64748b', marginTop: 0, marginBottom: 12, fontSize: 13 }}>
        {t('sp_refund_policy_desc','Course + Expedia rate refund policies always override yours — the customer sees the most-restrictive composed schedule at booking. Define your floor here.')}
      </p>
      {/* EPIC #92 P5 — refund policy is parent-locked; a disabled fieldset
          greys out every tier input + the add/remove buttons for a child. */}
      <fieldset disabled={pricingLocked} style={{ border: 'none', padding: 0, margin: 0, minInlineSize: 'auto' }}>
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: '#f8fafc' }}>
            <tr>
              <th style={th}>{t('sp_col_from_days_before','From days before')}</th>
              <th style={th}>{t('sp_col_to_days_before','To days before (blank = open)')}</th>
              <th style={th}>{t('sp_col_refund_pct','Refund %')}</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {tierRows.map((r, i) => (
              <tr key={i} style={{ borderTop: '1px solid #f1f5f9' }}>
                <td style={td}><input type="number" min={0} style={{ ...inp, width: 100 }} value={r.daysBeforeMin} onChange={e => updateTier(i, 'daysBeforeMin', e.target.value)} /></td>
                <td style={td}><input type="number" min={0} style={{ ...inp, width: 140 }} value={r.daysBeforeMax ?? ''} onChange={e => updateTier(i, 'daysBeforeMax', e.target.value === '' ? null : e.target.value)} /></td>
                <td style={td}><input type="number" min={0} max={100} step="0.01" style={{ ...inp, width: 100 }} value={r.refundPercent} onChange={e => updateTier(i, 'refundPercent', e.target.value)} /></td>
                <td style={td}><button onClick={() => removeTier(i)} style={{ ...secondaryBtn, color: '#dc2626', borderColor: '#fecaca' }}>{t('sp_remove','Remove')}</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ marginTop: 10 }}>
        <button onClick={addTier} style={secondaryBtn}>{t('sp_btn_add_tier','+ Add tier')}</button>
      </div>
      </fieldset>

      {/* ─── Payment options ─────────────────────────────────────────── */}
      <SectionHeader>{t('sp_payment_options','Payment options')}</SectionHeader>
      <div style={{ background: '#f8fafc', borderRadius: 6, padding: 14, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <Toggle checked={f.allowSplitPayments} onChange={(next) => set('allowSplitPayments', next)} />
          <span style={{ fontSize: 13, color: '#0f172a' }} onClick={() => set('allowSplitPayments', !f.allowSplitPayments)}>
            <strong>{t('sp_allow_split_payments','Allow split payments')}</strong>
            <span style={{ color: '#64748b', marginLeft: 8, fontSize: 12 }}>{t('sp_split_payments_desc','Primary pays their share; other golfers pay via emailed link.')}</span>
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Toggle checked={f.allowInstallments} onChange={(next) => set('allowInstallments', next)} />
          <span style={{ fontSize: 13, color: '#0f172a' }} onClick={() => set('allowInstallments', !f.allowInstallments)}>
            <strong>{t('sp_allow_installments','Allow installments')}</strong>
            <span style={{ color: '#64748b', marginLeft: 8, fontSize: 12 }}>{t('sp_installments_desc','Trips 30+ days out only. First payment covers hotel; remainder before tee-time.')}</span>
          </span>
        </div>
        {f.allowInstallments && (
          <div style={{ marginTop: 10, marginLeft: 50, fontSize: 13 }}>
            {t('sp_package_max_installments','Max installments:')} <input type="number" min={2} max={12} value={f.installmentCount} onChange={e => set('installmentCount', e.target.value)} style={{ ...inp, width: 70, display: 'inline-block', marginLeft: 6 }} />
          </div>
        )}
      </div>

      {/* ─── Sticky save bar ─────────────────────────────────────────── */}
      <div style={{
        position: 'sticky', bottom: 0, marginTop: 24,
        background: '#fff', borderTop: '1px solid #e2e8f0',
        padding: '16px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={archive} style={{ ...secondaryBtn, color: '#dc2626', borderColor: '#fecaca' }}>{t('sp_btn_archive_package','Archive package')}</button>
          <button onClick={deletePermanently} style={{ ...secondaryBtn, color: '#b91c1c', borderColor: '#fca5a5', background: '#fef2f2' }}>{t('sp_btn_delete_package','Delete permanently')}</button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {savedFlash && <span style={{ color: '#166534', fontSize: 13, fontWeight: 600 }}>{t('sp_saved_check','✓ Saved')}</span>}
          <button onClick={save} disabled={saving} style={{ ...primaryBtn, padding: '12px 28px', opacity: saving ? 0.6 : 1 }}>
            {saving ? t('sp_saving','Saving…') : t('sp_save_changes','Save changes')}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Cost breakdown (live margin estimator) ────────────────────────────
//
// Mirrors the regular Stay & Play breakdown card. Computes the worst-case
// hotel + golf cost based on the caps, subtracts from the package price,
// flags a red warning if the package would lose money at the upper bound.
//
// Per-course mode: sums each course's MaxPricePerRound × MaxRounds × NumGolfers.
// Apply-all mode: DefaultMaxGolfPrice × MaxRounds (largest of any course, or 1)
// × NumGolfers × number of courses included.
/**
 * Compute the worst-case breakdown numbers used by both CostBreakdown
 * (visible UI) and the form-level "block save when losing money" gate.
 * Returns null when there's no package price yet.
 */
function computeBreakdown({
  price, nights, roomCount, numGolfers, roundsPerGolfer,
  maxHotelPricePerNight, defaultMaxGolfPrice,
  applyAllCourses, courseRows, feesCtx,
  feeOverrideType, feeOverrideValue,
}, tr) {
  // tr is an optional translator (t from useLanguage). When the function is
  // called purely for the losing-money gate (labels not displayed) it can be
  // omitted; default to a passthrough so English text still renders.
  const txt = tr || ((k, f) => f)
  const p = Number(price) || 0
  if (!p) return null

  const n   = Number(numGolfers)      || 1
  const nt  = Number(nights)          || 1
  const r   = Number(roomCount)       || 1
  const rpg = Number(roundsPerGolfer) || 1
  const hCap = Number(maxHotelPricePerNight) || 0
  const totalHotel = hCap * nt * r

  const totalRounds = n * rpg
  let worstCap = 0
  let golfBreakdown = []
  if (applyAllCourses) {
    worstCap = Number(defaultMaxGolfPrice) || 0
  } else {
    for (const c of courseRows) {
      const cap = Number(c.maxPricePerRound) || 0
      if (cap > worstCap) worstCap = cap
      if (cap > 0) golfBreakdown.push({ name: c.courseName, cap })
    }
  }
  const totalGolf = totalRounds * worstCap

  // Taxes
  const taxRates = feesCtx?.taxRates || []
  const taxLines = []
  let totalTax = 0
  let runningCompoundBase = p
  for (const t of taxRates) {
    if (!t.rate || p <= 0) continue
    const base = t.baseMode === 'compound' ? runningCompoundBase : p
    const amt = Math.round(base * t.rate * 100) / 100
    if (amt <= 0) continue
    taxLines.push({
      label: `${t.name} (${(t.rate * 100).toFixed(3).replace(/\.?0+$/, '')}%${t.baseMode === 'compound' ? txt('sp_tax_compound_suffix_2', ', compound') : ''})`,
      amt,
    })
    totalTax += amt
    if (t.baseMode === 'compound') runningCompoundBase += amt
  }
  const customerTotal = p + totalTax

  // Fees — InteGolf's (provider 'integolf') and, in Mode B, the reseller's own
  // (provider 'reseller'). Only InteGolf's reduce the reseller's revenue today;
  // the reseller's fees are display-only this phase (their own money — the
  // course-payout split lands with the payout work). combineFees collapses all
  // fee lines into a single "Booking Fee" for display.
  const fees = feesCtx?.fees || {}
  const paymentMode = feesCtx?.paymentMode || 'A'
  const combineFees = !!feesCtx?.combineFees
  const feeLines = []
  let integolfFees = 0   // reduces revenue (unchanged behavior)
  let allFees = 0        // integolf + reseller (for the combined "Booking Fee")
  function applyPctFee(feeKey, base, ctxLabel) {
    const fee = fees[feeKey]
    if (!fee || !fee.percent || base <= 0) return
    let amt = base * fee.percent
    if (fee.roundUpDollar) amt = Math.ceil(amt)
    else amt = Math.round(amt * 100) / 100
    if (amt <= 0) return
    const provider = fee.provider || 'integolf'
    feeLines.push({
      label: `${fee.label} (${(fee.percent * 100).toFixed(2)}${txt('sp_package_pct_of', '% of')} ${ctxLabel})${fee.isOverride ? txt('sp_fee_custom_rate_suffix', ' · custom rate') : ''}`,
      amt, provider,
    })
    allFees += amt
    if (provider === 'integolf') integolfFees += amt
  }
  // Custom package fee override (super-admin-gated). When active it REPLACES the
  // *platform* fee — both the InteGolf platform_fee (Mode A) and the reseller's
  // own platform cut (reseller_platform_fee, Mode B). The resolved fee is split
  // between InteGolf (reduces revenue) and the reseller (retained, display-only)
  // per the super-admin-set share. Processing (gateway) fees are NOT replaced:
  // the Mode A InteGolf gateway_fee (3.49%) and the Mode B reseller_gateway_fee
  // (2.9%) still apply, since those are real card-processing pass-throughs.
  const ovType = feeOverrideType || 'none';
  const ovVal  = Number(feeOverrideValue);
  const ovActive = ovType !== 'none' && Number.isFinite(ovVal) && ovVal > 0;
  if (ovActive) {
    const F = ovType === 'percent'
      ? Math.round(customerTotal * (ovVal / 100) * 100) / 100
      : Math.round(ovVal * 100) / 100;
    const split = feesCtx?.feeOverrideSplit || { integolfPct: 100, resellerPct: 0 };
    const igPct = Number(split.integolfPct);
    const igAmt = Math.round(F * (igPct / 100) * 100) / 100;  // InteGolf's cut — the only part that reduces the reseller's net
    // Single "Booking Fee" line for the whole override — labelled plainly, with
    // no InteGolf/reseller split or rate basis. The split is a settlement
    // detail: the reseller's revenue still follows it (only InteGolf's share,
    // igAmt, reduces the net below), it's just not displayed.
    feeLines.push({
      label: txt('sp_booking_fee', 'Booking Fee'),
      amt: F, provider: 'booking', isOverride: true,
    });
    allFees += F; integolfFees += igAmt;
  } else {
    applyPctFee('platform_fee', customerTotal, txt('sp_fee_pct_of_customer_total', 'customer total incl. taxes'))
  }
  if (paymentMode === 'A') applyPctFee('gateway_fee', customerTotal, txt('sp_fee_pct_of_customer_total', 'customer total incl. taxes'))
  // Reseller-owned fees (only present in the payload when Mode B + configured).
  // A package fee override replaces the reseller's platform cut but not their
  // gateway (card-processing) pass-through — same split as InteGolf's
  // platform_fee vs gateway_fee above.
  if (!ovActive) applyPctFee('reseller_platform_fee', customerTotal, txt('sp_fee_pct_of_customer_total', 'customer total incl. taxes'))
  applyPctFee('reseller_gateway_fee', customerTotal, txt('sp_fee_pct_of_customer_total', 'customer total incl. taxes'))
  const hasResellerFees = feeLines.some(f => f.provider === 'reseller')

  // Worst-case revenue.
  // With a fee override, the reseller treats the FULL fees (booking + gateway)
  // as coming out of the price. Everything else in the breakdown is pre-tax
  // (package price + hotel + golf), but the fees are computed on the
  // tax-inclusive customer total — so restate each fee pre-tax
  // (× p / customerTotal) before subtracting. This keeps the whole breakdown in
  // pre-tax dollars, so the fee lines shown add up to the revenue below.
  // Without an override, only InteGolf's fees reduce the net (unchanged).
  let remaining
  if (ovActive) {
    const preTaxFactor = customerTotal > 0 ? p / customerTotal : 1
    for (const f of feeLines) f.amt = Math.round(f.amt * preTaxFactor * 100) / 100
    allFees = feeLines.reduce((s, f) => s + f.amt, 0)
    remaining = p - totalHotel - totalGolf - allFees
  } else {
    remaining = p - totalHotel - totalGolf - integolfFees
  }
  return {
    p, n, nt, r, rpg, totalRounds,
    totalHotel, totalGolf, worstCap, golfBreakdown,
    taxLines, totalTax, customerTotal,
    feeLines, totalFees: integolfFees, allFees, combineFees, hasResellerFees,
    overrideActive: ovActive,
    remaining, isNegative: remaining < 0,
    paymentMode,
  }
}

const feeOverrideInp = { padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14, outline: 'none' }

/**
 * FeeOverrideEditor — Expedia-style "add an agency service charge" box shown
 * only to resellers the super admin has enabled. Enter a percent OR a flat $;
 * the two are linked (editing one derives the other from the customer total).
 * The resolved fee replaces the standard InteGolf platform fee in the breakdown
 * above and is split InteGolf / You per the super-admin-set share. Display-only
 * estimate for now.
 */
function FeeOverrideEditor({ type, value, customerTotal, split, onChange }) {
  const { t } = useLanguage()
  const active = type && type !== 'none' && value !== '' && value != null
  const total = Number(customerTotal) || 0
  const val = Number(value)
  const asPercent = type === 'flat'    && total > 0 && Number.isFinite(val) ? (val / total * 100) : null
  const asDollar  = type === 'percent' && Number.isFinite(val)              ? (total * val / 100)  : null

  const clean = (v) => v.replace(/[^0-9.]/g, '')
  return (
    <div style={{ marginTop: 12, padding: 14, border: '1px dashed #cbd5e1', borderRadius: 8, background: '#fff' }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>
        {t('sp_fee_override_title', 'Custom booking fee (override)')}
      </div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label style={{ fontSize: 12, color: '#334155', display: 'flex', flexDirection: 'column', gap: 3 }}>
          {t('sp_fee_override_percent', 'Percent')}
          <input
            value={type === 'percent' ? value : (asPercent != null ? asPercent.toFixed(2) : '')}
            onChange={e => { const c = clean(e.target.value); onChange(c === '' ? 'none' : 'percent', c) }}
            inputMode="decimal" placeholder="0.00%"
            style={{ ...feeOverrideInp, width: 120 }}
          />
        </label>
        <span style={{ color: '#94a3b8', fontSize: 18, paddingBottom: 6 }} aria-hidden="true">⇄</span>
        <label style={{ fontSize: 12, color: '#334155', display: 'flex', flexDirection: 'column', gap: 3 }}>
          {t('sp_fee_override_flat', 'Fixed amount')}
          <input
            value={type === 'flat' ? value : (asDollar != null ? asDollar.toFixed(2) : '')}
            onChange={e => { const c = clean(e.target.value); onChange(c === '' ? 'none' : 'flat', c) }}
            inputMode="decimal" placeholder="$0.00"
            style={{ ...feeOverrideInp, width: 140 }}
          />
        </label>
        {active && (
          <button type="button" onClick={() => onChange('none', '')}
            style={{ marginBottom: 2, padding: '7px 12px', fontSize: 12, background: '#fff', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer' }}>
            {t('sp_fee_override_clear', 'Clear (use standard fee)')}
          </button>
        )}
      </div>
      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8 }}>
        {t('sp_fee_override_base_note', 'Percentage is calculated on the customer total incl. taxes.')}
      </div>
    </div>
  )
}

function CostBreakdown({
  price, nights, roomCount, numGolfers, roundsPerGolfer,
  maxHotelPricePerNight, defaultMaxGolfPrice, maxRevenueCap,
  applyAllCourses, courseRows, feesCtx,
  feeOverrideType, feeOverrideValue,
}) {
  const { t } = useLanguage()
  const calc = computeBreakdown({
    price, nights, roomCount, numGolfers, roundsPerGolfer,
    maxHotelPricePerNight, defaultMaxGolfPrice,
    applyAllCourses, courseRows, feesCtx,
    feeOverrideType, feeOverrideValue,
  }, t)
  if (!calc) return null
  // Destructure the bits the JSX needs; keeps the rest of the component
  // unchanged below.
  const {
    p, n, nt, r, rpg, totalRounds,
    totalHotel, totalGolf, worstCap, golfBreakdown,
    taxLines, totalTax, customerTotal,
    feeLines, allFees, combineFees, overrideActive,
    remaining, isNegative,
  } = calc
  const integolfLines = feeLines.filter(f => f.provider === 'integolf')
  const resellerLines = feeLines.filter(f => f.provider === 'reseller')
  const hCap = Number(maxHotelPricePerNight) || 0

  return (
    <div style={{
      marginTop: 14, padding: 16, borderRadius: 8,
      background: isNegative ? '#fef2f2' : '#f0fdfa',
      border: '1px solid ' + (isNegative ? '#fecaca' : '#a7f3d0'),
    }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {t('sp_cost_breakdown_worst_case','Cost breakdown (worst case)')}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
        <Row label={t('sp_row_package_price_customer','Package price (customer pays)')} value={`$${p.toFixed(2)}`} bold />
        {totalHotel > 0 && (
          <Row
            label={t('sp_row_max_hotel_cost','Max hotel cost ({rooms} {roomWord} × {nights} {nightWord} × ${rate}/night)',{
              rooms: r,
              roomWord: r === 1 ? t('sp_room_lower','room') : t('sp_rooms_lower','rooms'),
              nights: nt,
              nightWord: nt === 1 ? t('sp_night_lower','night') : t('sp_nights_lower','nights'),
              rate: hCap,
            })}
            value={`-$${totalHotel.toFixed(2)}`} negative
          />
        )}
        {totalGolf > 0 && (
          <>
            <Row
              label={t('sp_row_max_golf_cost','Max golf cost ({golfers} {golferWord} × {rpg} {roundWord} = {totalRounds} rounds × ${cap} max/round)',{
                golfers: n,
                golferWord: n === 1 ? t('sp_golfer_lower','golfer') : t('sp_golfers_lower','golfers'),
                rpg,
                roundWord: rpg === 1 ? t('sp_round_lower','round') : t('sp_rounds_lower','rounds'),
                totalRounds,
                cap: worstCap.toFixed(2),
              })}
              value={`-$${totalGolf.toFixed(2)}`} negative
            />
            {!applyAllCourses && golfBreakdown.length > 1 && (
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2, marginLeft: 4 }}>
                {t('sp_worst_case_uses_highest_cap','Worst-case uses the highest per-course cap. Per-course caps:')}{' '}
                {golfBreakdown.map((b, i) => (
                  <span key={b.name}>{i > 0 && ', '}{b.name} <code>${b.cap.toFixed(2)}</code></span>
                ))}
              </div>
            )}
          </>
        )}
        {taxLines.length > 0 && (
          <>
            <div style={subheadStyle}>
              {t('sp_taxes_added_on_top','Taxes (added on top, customer pays)')}
            </div>
            {taxLines.map(line => (
              <Row key={line.label} label={line.label} value={`+$${line.amt.toFixed(2)}`} muted />
            ))}
            <Row
              label={t('sp_row_total_customer_charge','Total customer charge (price + taxes)')}
              value={`$${customerTotal.toFixed(2)}`}
              bold
            />
          </>
        )}
        {feeLines.length > 0 && (
          combineFees ? (
            <>
              <div style={subheadStyle}>{t('sp_fees_subhead','Fees (on gross charge)')}</div>
              <Row label={t('sp_booking_fee','Booking Fee')} value={`-$${allFees.toFixed(2)}`} negative />
            </>
          ) : overrideActive ? (
            // Custom fee override: one neutral "Fees" section. The InteGolf /
            // reseller split is intentionally NOT shown — revenue still follows
            // the reseller's configured split (only InteGolf's share is netted
            // out below).
            <>
              <div style={subheadStyle}>{t('sp_fees_subhead','Fees (on gross charge)')}</div>
              {feeLines.map(line => (
                <Row key={line.label} label={line.label} value={`-$${line.amt.toFixed(2)}`} negative />
              ))}
            </>
          ) : (
            <>
              {integolfLines.length > 0 && (
                <>
                  <div style={subheadStyle}>{t('sp_integolf_fees_on_gross','InteGolf fees (on gross charge)')}</div>
                  {integolfLines.map(line => (
                    <Row key={line.label} label={line.label} value={`-$${line.amt.toFixed(2)}`} negative />
                  ))}
                </>
              )}
              {resellerLines.length > 0 && (
                <>
                  <div style={subheadStyle}>{t('sp_your_fees_on_gross','Your fees (on gross charge)')}</div>
                  {resellerLines.map(line => (
                    <Row key={line.label} label={line.label} value={`-$${line.amt.toFixed(2)}`} negative />
                  ))}
                </>
              )}
            </>
          )
        )}
        <div style={{
          borderTop: '1px solid ' + (isNegative ? '#fca5a5' : '#86efac'),
          paddingTop: 8, marginTop: 4,
          display: 'flex', justifyContent: 'space-between',
        }}>
          <span style={{ fontWeight: 700, color: isNegative ? '#dc2626' : '#166534' }}>
            {t('sp_estimated_revenue_worst_case','Estimated revenue (worst case)')}
          </span>
          <strong style={{ color: isNegative ? '#dc2626' : '#166534', fontSize: 15 }}>
            ${remaining.toFixed(2)}
          </strong>
        </div>
        {maxRevenueCap !== '' && maxRevenueCap != null && Number(maxRevenueCap) > 0 && (
          <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed #cbd5e1', fontSize: 12.5, color: '#0f766e', display: 'flex', alignItems: 'center', gap: 6 }}>
            🧢 {t('sp_maxrev_capped_line', 'Best case: revenue capped at {amount}', { amount: `$${Number(maxRevenueCap).toFixed(2)}` })}
          </div>
        )}
      </div>
      {isNegative && (
        <div style={{ marginTop: 10, fontSize: 12, color: '#dc2626', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
          {t('sp_costs_exceed_price_warning',"⚠ Costs exceed the package price. You'll lose money at the upper bound — raise the price, or lower hotel/golf caps.")}
        </div>
      )}
      {!isNegative && (totalHotel > 0 || totalGolf > 0) && (
        <div style={{ marginTop: 8, fontSize: 11, color: '#64748b', lineHeight: 1.4 }}>
          {t('sp_worst_case_actual_lower','Worst case: every customer maxes out the caps. Actual cost is usually lower since dynamic pricing means most tee times come in below the cap.')}
        </div>
      )}
    </div>
  )
}
/**
 * HotelAvailabilityHint — small banner under the hotel-max input that
 * shows live Expedia availability for the typed budget. Re-fired on blur
 * of the input, not on every keystroke (rate-limit + cost concerns).
 */
function HotelAvailabilityHint({ check, checking, groupingId }) {
  const { t } = useLanguage()
  if (checking) {
    return (
      <div style={{ marginTop: 6, padding: '6px 10px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 6, fontSize: 12, color: '#0284c7', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ display: 'inline-block', width: 12, height: 12, border: '2px solid #0284c7', borderTopColor: 'transparent', borderRadius: '50%', animation: 'hotelSpin 0.8s linear infinite' }} />
        {t('sp_checking_expedia_hotels','Searching for hotels in this area…')}
        <style>{`@keyframes hotelSpin { to { transform: rotate(360deg) } }`}</style>
      </div>
    )
  }
  if (!check) return null
  // When the grouping has filter rules, the headline number is "matching
  // filters" — that's what the customer actually sees. The original
  // `available` becomes the "could unlock" reference.
  const filterAware = check.hasFilters && check.availableMatchingFilters != null
  const headline = filterAware ? check.availableMatchingFilters : check.available
  const good = headline > 2
  const okay = headline > 0 && !good
  const bg = good ? '#ecfdf5' : okay ? '#fffbeb' : '#fef2f2'
  const border = good ? '#a7f3d0' : okay ? '#fde68a' : '#fecaca'
  const fg = good ? '#065f46' : okay ? '#854d0e' : '#991b1b'
  const icon = good ? '✓' : okay ? '⚠' : '✗'
  const minStarsStr = check.minStars ? t('sp_package_stars_suffix',' {n}+ star',{ n: check.minStars }) : ''
  const headlineMsg = filterAware
    ? t('sp_hotels_match_filters_budget','{count}{stars} hotel{plural} match your filters under ${budget}/night',{
        count: headline,
        stars: minStarsStr,
        plural: headline === 1 ? '' : 's',
        budget: check.budget,
      })
    : check.message
  return (
    <div style={{ marginTop: 6, padding: '8px 10px', background: bg, border: `1px solid ${border}`, borderRadius: 6, fontSize: 12, color: fg }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 14 }}>{icon}</span>
        <strong>{headlineMsg}</strong>
        {check.total > 0 && <span style={{ color: '#64748b', fontWeight: 400 }}>{t('sp_total_in_radius','({count} total in radius)',{ count: check.total })}</span>}
      </div>

      {/* Filter-blocked CTA — when filters are narrowing things, surface
          a one-click button that auto-relaxes the minimal set of rules
          to unblock every otherwise-eligible hotel. Backend computed the
          relaxedRules object greedily; we just save it. */}
      {filterAware && check.blockedByFilters > 0 && (
        <RelaxFiltersInline
          blocked={check.blockedByFilters}
          relaxedRules={check.relaxedRules}
          relaxedKeys={check.relaxedKeys || []}
          groupingId={groupingId}
        />
      )}

      {check.suggestion && !check.suggestion.lowerStars && (
        <div style={{ marginTop: 6, paddingLeft: 22, color: '#92400e' }}>
          {t('sp_package_try_budget_unlock','💡 Try ${budget}/night to unlock ~{count} options',{ budget: check.suggestion.budget, count: check.suggestion.wouldUnlock })}
          {check.suggestion.cheapest && (
            <>{' '}{t('sp_package_cheapest_nearby_at','· cheapest nearby: {hotel} at ${nightly}/night',{ hotel: check.suggestion.cheapest.hotel, nightly: check.suggestion.cheapest.nightly })}</>
          )}
        </div>
      )}
    </div>
  )
}

// Inline "show me those hotels" — saves the relaxedRules object directly
// to the grouping so the customer iframe + portal preview both update.
// We translate the dropped rule keys into human-readable labels so the
// reseller knows exactly what just changed.
// Maps each relaxable hotel-filter rule key to its translation key + English
// fallback so the displayed "Will clear: …" labels localize at render time.
const RELAX_KEY_LABELS = {
  requiredAmenities:    ['sp_required_amenities',   'Required amenities'],
  themes:               ['sp_themes',               'Themes'],
  propertyCategories:   ['sp_property_categories',   'Property categories'],
  allowedBrands:        ['sp_allowed_brands',        'Allowed brands'],
  excludedBrands:       ['sp_excluded_brands',       'Excluded brands'],
  minReviewCount:       ['sp_min_review_count',      'Min review count'],
  minGuestRating:       ['sp_min_guest_rating',      'Min guest rating'],
  minStarRating:        ['sp_min_star_rating',       'Min star rating'],
  refundableOnly:       ['sp_relax_refundable_only', 'Refundable only'],
  onlyAllInclusive:     ['sp_only_all_inclusive',    'Only all-inclusive'],
  excludeAllInclusive:  ['sp_exclude_all_inclusive', 'Exclude all-inclusive'],
}
function RelaxFiltersInline({ blocked, relaxedRules, relaxedKeys, groupingId }) {
  const { t } = useLanguage()
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  async function relax() {
    if (!relaxedRules || !relaxedKeys?.length) return
    setBusy(true)
    try {
      await tournamentApi.put(`/portal/reseller/groupings/${groupingId}`, { hotelFilters: relaxedRules })
      setDone(true)
    } catch (e) {
      alert(e?.response?.data?.message || e.message)
    } finally { setBusy(false) }
  }
  if (done) {
    return (
      <div style={{ marginTop: 6, paddingLeft: 22, color: '#065f46' }}>
        {t('sp_filters_updated_reblur','✓ Filters updated. Re-blur the price field to refresh the count.')}
      </div>
    )
  }
  const labels = (relaxedKeys || []).map(k => {
    const m = RELAX_KEY_LABELS[k]
    return m ? t(m[0], m[1]) : k
  })
  return (
    <div style={{ marginTop: 6, paddingLeft: 22, color: '#92400e' }}>
      {t('sp_more_hotels_blocked_filters','🔍 {count} more hotel{plural} fit your price + star caps but are blocked by your filter rules.',{ count: blocked, plural: blocked === 1 ? '' : 's' })}{' '}
      <button
        type="button"
        disabled={busy || !relaxedKeys?.length}
        onClick={relax}
        style={{
          marginLeft: 4, padding: '4px 10px', fontSize: 12, fontWeight: 600,
          background: '#fff', color: '#92400e', border: '1px solid #fde68a',
          borderRadius: 4, cursor: busy ? 'wait' : 'pointer',
        }}
      >
        {busy ? t('sp_btn_updating','Updating…') : t('sp_btn_update_filters','Update filters')}
      </button>
      {labels.length > 0 && (
        <div style={{ fontSize: 11, color: '#a16207', marginTop: 4 }}>
          {t('sp_will_clear','Will clear: {labels}',{ labels: labels.join(', ') })}
        </div>
      )}
    </div>
  )
}

function Row({ label, value, bold, negative, muted }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
      <span style={{ color: '#64748b' }}>{label}</span>
      <strong style={{ color: negative ? '#dc2626' : (muted ? '#475569' : '#0f172a'), fontWeight: bold ? 700 : 600 }}>{value}</strong>
    </div>
  )
}

const subheadStyle = {
  marginTop: 8, paddingTop: 8,
  borderTop: '1px dashed #cbd5e1',
  fontSize: 11, fontWeight: 700,
  color: '#64748b',
  textTransform: 'uppercase', letterSpacing: 0.5,
}

// ─── Add-ons (inline list with own CRUD) ────────────────────────────────
function AddonsList({ pkg, addons, onChanged, locked = false }) {
  const { t } = useLanguage()
  const [showAdd, setShowAdd] = useState(false)
  const [editing, setEditing] = useState(null)

  async function remove(addonId) {
    if (!confirm(t('sp_confirm_remove_addon','Remove this add-on?'))) return
    try { await tournamentApi.delete(`/portal/reseller/packages/${pkg.ID_NO}/addons/${addonId}`); onChanged() }
    catch (e) { alert(e?.response?.data?.message || e.message) }
  }
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: 13, color: '#475569' }}>{t('sp_addon_count','{count} add-on{plural}',{ count: addons.length, plural: addons.length === 1 ? '' : 's' })}</span>
        {!locked && <button onClick={() => setShowAdd(s => !s)} style={secondaryBtn}>{showAdd ? t('cancel','Cancel') : t('sp_add_plus','+ Add')}</button>}
      </div>
      {!locked && showAdd && <AddonForm pkg={pkg} onSaved={() => { setShowAdd(false); onChanged() }} onCancel={() => setShowAdd(false)} />}
      {addons.length === 0 && !showAdd ? (
        <div style={empty}>{t('sp_no_addons','No add-ons.')}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {addons.map(a => editing === a.ID_NO ? (
            <AddonForm key={a.ID_NO} pkg={pkg} addon={a} onSaved={() => { setEditing(null); onChanged() }} onCancel={() => setEditing(null)} />
          ) : (
            <div key={a.ID_NO} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 14, display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ flex: 1 }}>
                <strong style={{ fontSize: 14, color: '#0f172a' }}>{a.Name}{!a.IsOptional && <span style={{ marginLeft: 8, fontSize: 11, color: '#dc2626', fontWeight: 600 }}>{t('sp_package_addon_required_badge','REQUIRED')}</span>}</strong>
                {a.Description && <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{a.Description}</div>}
                <div style={{ fontSize: 12, color: '#475569', marginTop: 4 }}>
                  {a.PricePerGolfer != null && <>{t('sp_price_per_golfer','${amount} / golfer',{ amount: Number(a.PricePerGolfer).toFixed(2) })}</>}
                  {a.PricePerGolfer != null && a.PricePerPackage != null && ' · '}
                  {a.PricePerPackage != null && <>{t('sp_price_per_package','${amount} / package',{ amount: Number(a.PricePerPackage).toFixed(2) })}</>}
                </div>
              </div>
              {!locked && <button onClick={() => setEditing(a.ID_NO)} style={secondaryBtn}>{t('edit','Edit')}</button>}
              {!locked && <button onClick={() => remove(a.ID_NO)} style={{ ...secondaryBtn, color: '#dc2626', borderColor: '#fecaca' }}>{t('sp_remove','Remove')}</button>}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function AddonForm({ pkg, addon, onSaved, onCancel }) {
  const { t } = useLanguage()
  const [name, setName] = useState(addon?.Name || '')
  const [description, setDescription] = useState(addon?.Description || '')
  const [perGolfer, setPerGolfer] = useState(addon?.PricePerGolfer != null ? String(addon.PricePerGolfer) : '')
  const [perPackage, setPerPackage] = useState(addon?.PricePerPackage != null ? String(addon.PricePerPackage) : '')
  const [isOptional, setIsOptional] = useState(addon ? !!addon.IsOptional : true)
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState(null)
  async function submit(e) {
    e.preventDefault()
    setErr(null); setSubmitting(true)
    try {
      const body = { name, description, pricePerGolfer: perGolfer || null, pricePerPackage: perPackage || null, isOptional }
      if (addon) await tournamentApi.put(`/portal/reseller/packages/${pkg.ID_NO}/addons/${addon.ID_NO}`, body)
      else       await tournamentApi.post(`/portal/reseller/packages/${pkg.ID_NO}/addons`, body)
      onSaved()
    } catch (e2) { setErr(e2?.response?.data?.message || e2.message) }
    finally { setSubmitting(false) }
  }
  return (
    <form onSubmit={submit} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16 }}>
      {err && <div style={errorBox}>{err}</div>}
      <Field label={t('sp_name_label','Name')}><input style={inp} required value={name} onChange={e => setName(e.target.value)} placeholder={t('sp_ph_addon_examples','Cart rental, Club rental, BBQ dinner…')} /></Field>
      <Field label={t('sp_description_optional','Description (optional)')}><textarea style={{ ...inp, minHeight: 60 }} value={description} onChange={e => setDescription(e.target.value)} /></Field>
      <div style={row}>
        <Field label={t('sp_label_price_per_golfer','Price per golfer')} style={{ flex: 1 }}><input type="number" step="0.01" min={0} style={inp} value={perGolfer} onChange={e => setPerGolfer(e.target.value)} placeholder={t('sp_ph_eg_2500','e.g. 25.00')} /></Field>
        <Field label={t('sp_label_or_price_per_package','OR price per package')} style={{ flex: 1 }}><input type="number" step="0.01" min={0} style={inp} value={perPackage} onChange={e => setPerPackage(e.target.value)} placeholder={t('sp_ph_eg_10000','e.g. 100.00')} /></Field>
      </div>
      <Field label=""><Toggle checked={!!isOptional} onChange={setIsOptional} label={t('sp_toggle_optional_opt_out','Optional (customer can opt out)')} /></Field>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" onClick={onCancel} style={secondaryBtn}>{t('cancel','Cancel')}</button>
        <button type="submit" disabled={submitting} style={{ ...primaryBtn, opacity: submitting ? 0.6 : 1 }}>{submitting ? t('sp_saving','Saving…') : (addon ? t('save','Save') : t('sp_btn_add_addon','Add add-on'))}</button>
      </div>
    </form>
  )
}

// ─── Building blocks ────────────────────────────────────────────────────
function SectionHeader({ children }) {
  return (
    <h3 style={{
      margin: '32px 0 12px', fontSize: 14, fontWeight: 700,
      color: '#0f172a', textTransform: 'uppercase', letterSpacing: 0.5,
      borderBottom: '2px solid #00d19f', paddingBottom: 6, display: 'inline-block',
    }}>{children}</h3>
  )
}
function Field({ label, hint, children, style }) {
  return (
    <div style={{ marginBottom: 14, ...style }}>
      {label && <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>{label}</label>}
      {children}
      {hint && <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{hint}</div>}
    </div>
  )
}

const inp = { width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }
const row = { display: 'flex', gap: 12 }
const errorBox = { padding: 12, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 6, marginBottom: 16 }
const empty = { padding: 30, textAlign: 'center', color: '#64748b', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }
const th = { padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 11, letterSpacing: 0.5, textTransform: 'uppercase' }
const td = { padding: '10px 12px', verticalAlign: 'middle' }
const primaryBtn = { padding: '10px 16px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }
const secondaryBtn = { padding: '8px 14px', background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, cursor: 'pointer' }
